import { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaUserCircle
} from "react-icons/fa";

import "./Sidebar.css";
import SidebarDiwaliJhalar from "./SidebarDiwaliJhalar";
import { SIDEBAR_PAGES } from "../config/permissionPages";
import { hasAccess, isFixedPage } from "../config/permissionAccess";

function getRoleFromToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "admin";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return (payload.role || "admin").toLowerCase();
  } catch {
    return "admin";
  }
}

export default function Sidebar() {
  const location = useLocation();
  const role = getRoleFromToken();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const permissions = user.permissions || [];

  const [open, setOpen] = useState(false);        // mobile
  const [collapsed, setCollapsed] = useState(false); // icon-only
  const [search, setSearch] = useState("");      // 🔍 search
  const [expandedSections, setExpandedSections] = useState(new Set()); // expanded sections

  // mobile toggle from topbar
  useEffect(() => {
    const toggle = () => setOpen(o => !o);
    window.addEventListener("toggle-sidebar", toggle);
    return () => window.removeEventListener("toggle-sidebar", toggle);
  }, []);

  const pages = SIDEBAR_PAGES;

  const getVisibleSubPages = (page) => {
    if (!Array.isArray(page.children)) return [];
    const roleVisibleChildren = page.children.filter(
      (subPage) => !subPage.adminOnly || role === "admin"
    );
    if (role === "admin") return roleVisibleChildren;
    if (!isFixedPage(page.permissionId) && hasAccess(page.permissionId, permissions)) {
      return roleVisibleChildren;
    }
    return roleVisibleChildren.filter((subPage) => hasAccess(subPage.permissionId, permissions));
  };

  // Auto-expand the section that contains the active route.
  useEffect(() => {
    const activeSection = pages.find(
      (p) =>
        Array.isArray(p.children) &&
        p.children.some((sub) => location.pathname === sub.path)
    );
    if (!activeSection) return;

    setExpandedSections((prev) => {
      if (prev.has(activeSection.id)) return prev;
      const next = new Set(prev);
      next.add(activeSection.id);
      return next;
    });
  }, [location.pathname]);

  // 🔥 TOGGLE SECTION FUNCTION
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // 🔥 SEARCH + PERMISSION FILTER
  const filteredPages = useMemo(() => {
    return pages
      .filter((p) => !p.adminOnly || role === "admin")
      .filter((page) => {
        if (role === "admin") return true;
        if (Array.isArray(page.children)) {
          return getVisibleSubPages(page).length > 0;
        }
        return hasAccess(page.permissionId, permissions);
      })
      .filter((page) => page.label.toLowerCase().includes(search.toLowerCase()));
  }, [search, role, permissions]);

  return (
    <>
      {open && <div className="sidebar-overlay" onClick={() => setOpen(false)} />}

      <div className={`sidebar ${open ? "open" : ""} ${collapsed ? "collapsed" : ""}`}>
        <SidebarDiwaliJhalar />

        {/* 🔘 COLLAPSE BUTTON */}
        <button
  className="collapse-btn"
  onClick={() => {
    setCollapsed(!collapsed);

    // 🔥 TOPBAR SYNC
    if (!collapsed) {
      document.body.classList.add("sidebar-collapsed");
    } else {
      document.body.classList.remove("sidebar-collapsed");
    }
}}
>

          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>

        {/* 🔍 SEARCH BOX (hide when collapsed) */}
        {!collapsed && (
        <div className="sidebar-search">
  <i className="fa fa-search search-icon"></i>
  <input
    type="text"
    placeholder="Search menu..."
    value={search}
    onChange={e => setSearch(e.target.value)}
  />
</div>

        )}

        <ul className="sidebar-links">
          {filteredPages.map(p => (
            <li key={p.id}>
              {Array.isArray(p.children) ? (
                // Expandable section
                <div className="sidebar-section">
                  <div 
                    className={`sidebar-section-header ${expandedSections.has(p.id) ? 'expanded' : ''}`}
                    onClick={() => toggleSection(p.id)}
                  >
                    <div className="section-main">
                      <p.icon />
                      {!collapsed && <span>{p.label}</span>}
                    </div>
                    {!collapsed && (
                      <div className="section-toggle">
                        {expandedSections.has(p.id) ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    )}
                  </div>
                  
                  {/* Sub-pages */}
                  {expandedSections.has(p.id) && !collapsed && (
                    <div className="sub-pages">
                      {getVisibleSubPages(p).map(subPage => (
                        <Link 
                          key={subPage.permissionId} 
                          to={subPage.path} 
                          className={`sub-page-link ${location.pathname === subPage.path ? 'active' : ''}`}
                        >
                          <subPage.icon />
                          <span>{subPage.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Regular link
                <Link 
                  to={p.path} 
                  className={location.pathname === p.path ? "active" : ""}
                  title={collapsed ? p.label : ""}
                >
                  <p.icon />
                  {!collapsed && <span>{p.label}</span>}
                </Link>
              )}
            </li>
          ))}
        </ul>
        <div className="sidebar-profile" onClick={() => navigate("/profile")}>
          <div className="profile-inner">
            <FaUserCircle className="profile-icon" />
            {!collapsed && (
              <div className="profile-info">
                <span className="profile-name">
                  {user?.name || "User"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
